import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useEffectiveUser } from '../contexts/AuthContext';
import Header from '../components/Header';

function Notification() {
  const [notifications, setNotifications] = useState([]);
  const { currentUser } = useEffectiveUser();

  useEffect(() => {
    if (currentUser) {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const notifs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));
        setNotifications(notifs);
      });

      return () => unsubscribe();
    }
  }, [currentUser]);

  const handleNotificationClick = async (notification) => {
    const notificationRef = doc(db, 'notifications', notification.id);
    await updateDoc(notificationRef, {
      read: true
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    let date;
    
    // Convert to Date object
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    // Get today's date
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Format time
    const time = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });

    // Check if it's today, yesterday, or older
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${time}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${time}`;
    } else {
      return `${date.toLocaleDateString()} at ${time}`;
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-4 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Notifications
            </h1>
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              {notifications.length} {notifications.length === 1 ? 'notification' : 'notifications'}
            </span>
          </div>
          
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    p-4 sm:p-6 border-b dark:border-gray-700 last:border-0 
                    cursor-pointer transition-colors duration-200
                    ${!notification.read 
                      ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                  `}
                >
                  <div className="flex justify-between items-start gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm sm:text-base ${!notification.read 
                        ? 'text-gray-900 dark:text-white font-medium' 
                        : 'text-gray-600 dark:text-gray-300'}`}>
                        {notification.message}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 sm:mt-2">
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                    <span className={`text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full ${
                      notification.read 
                        ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                    }`}>
                      {notification.read ? 'Read' : 'New'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 sm:py-12 px-3 sm:px-4">
                <div className="text-center">
                  <p className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 mb-2">
                    No notifications
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500">
                    You're all caught up! Check back later for new notifications.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Notification;
